/* eslint-disable react/no-array-index-key */
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import platform from '@propertypal/shared/src/utils/platform';
import React, { FunctionComponent } from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Container, Content, Item, MobileWrapper } from './PageNumbers.style';

interface Props {
  currentUrl: string;
  currentPage: number;
  totalPages: number;
  useQueryString?: boolean;
}

const pageRegex = /(page-\d+|page=\d+)/;
const DELTA = 5;
const MOBILE_DELTA = 3;
const BREAKPOINT = 880;

const getPageNumbers = (currentPage: number, totalPages: number, delta: number) => {
  const pages = [];

  if ((totalPages < 10 && delta > 2) || totalPages <= 6) {
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(i);
    }
  } else if (currentPage <= delta + 1) {
    for (let i = 1; i < delta * 2; i += 1) {
      pages.push(i);
    }
    pages.push('...', totalPages);
  } else if (currentPage > totalPages - delta - 1) {
    pages.push(1, '...');
    for (let i = totalPages - delta * 2 + 2; i <= totalPages; i += 1) {
      pages.push(i);
    }
  } else {
    pages.push(1, '...');
    for (let i = currentPage - delta + 1; i < currentPage + delta; i += 1) {
      pages.push(i);
    }
    pages.push('...', totalPages);
  }

  return pages;
};

const getPageUrl = (page: number | string, url: string, useQueryString?: boolean) => {
  if (typeof page === 'number') {
    const pageNumber = page > 1 ? `page-${page}` : '';
    const queryNumber = `page=${page - 1}`;

    if (useQueryString) {
      return `${url}?${queryNumber}`;
    }

    if (pageRegex.test(url)) {
      return url.replace(/page-\d+/, pageNumber).replace(/page=\d+/, queryNumber);
    }

    if (url.includes('?')) {
      return `${url}&${queryNumber}`;
    }

    return `${url}/${pageNumber}`;
  }

  return '';
};

const PageNumbers: FunctionComponent<Props> = (props) => {
  const windowSize = useWindowSize();
  const delta = windowSize.width !== undefined && windowSize.width > BREAKPOINT ? DELTA : MOBILE_DELTA;
  const pages = getPageNumbers(props.currentPage, props.totalPages, delta);

  return (
    <Container>
      <Content>
        {props.currentPage > 1 && delta > 2 && (
          <Item
            $active={platform.select(true, false)}
            data-testid="page-arrow-left"
            aria-label="previous page"
            href={getPageUrl(props.currentPage - 1, props.currentUrl, props.useQueryString)}
          >
            {platform.select(<FontAwesomeIcon icon={faChevronLeft} />, <>Back</>)}
          </Item>
        )}

        {pages.map((page, index) => {
          if (page === '...') {
            return (
              <Item as="div" key={index} $noHover data-testid={`page-link-${page}`}>
                {page}
              </Item>
            );
          }

          return (
            <Item
              aria-label={`page ${page}`}
              $active={page === props.currentPage}
              data-testid={`page-link-${page}`}
              key={index}
              href={getPageUrl(page, props.currentUrl, props.useQueryString)}
            >
              {page}
            </Item>
          );
        })}

        {props.currentPage + 1 <= props.totalPages && delta > 2 && (
          <Item
            $active={platform.select(true, false)}
            data-testid="page-arrow-right"
            aria-label="next page"
            href={getPageUrl(props.currentPage + 1, props.currentUrl, props.useQueryString)}
          >
            {platform.select(<FontAwesomeIcon icon={faChevronRight} />, <>Next</>)}
          </Item>
        )}
      </Content>

      <MobileWrapper
        data-testid="mobile-page-numbers"
        hasPrev={props.currentPage > 1}
        hasNext={props.currentPage + 1 <= props.totalPages}
      >
        {props.currentPage > 1 && (
          <Item
            data-testid="mobile-page-arrow-left"
            aria-label="previous page"
            href={getPageUrl(props.currentPage - 1, props.currentUrl, props.useQueryString)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>Previous</span>
          </Item>
        )}
        {props.currentPage + 1 <= props.totalPages && (
          <Item
            data-testid="mobile-page-arrow-right"
            aria-label="next page"
            href={getPageUrl(props.currentPage + 1, props.currentUrl, props.useQueryString)}
          >
            <span>Next</span>
            <FontAwesomeIcon icon={faChevronRight} />
          </Item>
        )}
      </MobileWrapper>
    </Container>
  );
};

export default React.memo(PageNumbers);
